import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../styles/admin/AdminNewsForm.css';

/* global CKEDITOR */

function CKEditorLegacy({ initialData = '', onDataChange }) {
    const textareaRef = useRef(null);
    const editorInstance = useRef(null);
    const [isReady, setIsReady] = useState(false); // Theo dõi trạng thái CKEditor đã sẵn sàng

    // EFFECT 1: Khởi tạo và Cleanup (Chỉ chạy khi mount/unmount)
    useEffect(() => {
        // Wait for CKEDITOR to be available in the global scope
        if (typeof window.CKEDITOR === 'undefined') {
            console.error('CKEditor is not loaded. Make sure the CKEditor script is properly included in your HTML.');
            return;
        }

        if (textareaRef.current) {
            
            // Khởi tạo Editor
            editorInstance.current = CKEDITOR.replace(textareaRef.current, {
                height: 300,
                // Các cấu hình khác của CKEditor v3/v4...
            });

            // Lắng nghe sự kiện editor sẵn sàng
            editorInstance.current.on('instanceReady', (evt) => {
                setIsReady(true);
                // Set dữ liệu ban đầu
                evt.editor.setData(initialData);
            });

            // Lắng nghe sự kiện thay đổi nội dung
            editorInstance.current.on('change', () => {
                const data = editorInstance.current.getData();
                // ⭐️ GỌI CALLBACK để truyền dữ liệu về component cha ⭐️
                if (onDataChange) { 
                    onDataChange(data);
                }
            });
        }

        // CLEANUP: Hủy Editor
        return () => {
            if (editorInstance.current && typeof window.CKEDITOR !== 'undefined') {
                try {
                    // Kiểm tra CKEDITOR.instances để đảm bảo instance tồn tại trước khi destroy
                    if (CKEDITOR.instances && editorInstance.current.name && CKEDITOR.instances[editorInstance.current.name]) {
                        editorInstance.current.destroy(true); // true để loại bỏ element khỏi DOM
                    }
                } catch (error) {
                    console.error('Error while destroying CKEditor instance:', error);
                } finally {
                    editorInstance.current = null;
                }
            }
        };
    }, []); 

    // EFFECT 2: Đồng bộ dữ liệu từ prop initialData (Chạy khi dữ liệu cha thay đổi)
    useEffect(() => {
        // Cập nhật editor nếu dữ liệu initialData thay đổi sau khi load và editor đã sẵn sàng
        if (isReady && editorInstance.current && editorInstance.current.getData() !== initialData) {
            editorInstance.current.setData(initialData);
        }
    }, [initialData, isReady]);

    // **Bạn có thể giữ lại đoạn code hiển thị content debug nếu muốn, nhưng không còn cần state content nữa**
    // Để giữ lại cho mục đích debug, bạn có thể truyền content từ component cha xuống.
    
    return (
        <div className="w-full">
            <div className="bg-white border border-gray-300 rounded-lg shadow-sm overflow-hidden">
                <div className="p-3 border-b border-gray-200">
                    <h3 className="text-md font-semibold text-blue-700">Nội dung bài viết</h3>
                </div>
                
                <div className="p-0">
                    <div className="w-full">
                        <textarea 
                            ref={textareaRef} 
                            className="w-full h-80 hidden"
                            defaultValue={initialData}
                        />
                    </div>

                    <div className="p-4 bg-gray-50 border-t border-gray-200">
                        <p className="text-sm font-medium text-gray-600 mb-2">Xem trước:</p>
                        <div className="p-3 bg-white border border-gray-200 rounded-md max-h-40 overflow-y-auto">
                            <div 
                                className="text-sm text-gray-700"
                                dangerouslySetInnerHTML={{ __html: initialData || '<span class="text-gray-400">Nội dung sẽ hiển thị tại đây...</span>' }}
                            />
                        </div>
                    </div>
                    
                    {!isReady && (
                        <p className="p-2 text-xs text-amber-600">Đang tải trình soạn thảo...</p>
                    )}
                </div>
            </div>
            
            <style jsx global>{`
                /* Reset all CKEditor styles */
                .cke_chrome {
                    width: 100% !important;
                    border: none !important;
                    box-shadow: none !important;
                    margin: 0 !important;
                    display: flex !important;
                    flex-direction: column !important;
                }
                
                /* Toolbar */
                .cke_top {
                    background: #f9fafb !important;
                    border: none !important;
                    border-bottom: 1px solid #e5e7eb !important;
                    padding: 8px !important;
                    width: 100% !important;
                    box-sizing: border-box !important;
                }
                
                .cke_toolbox {
                    display: flex !important;
                    flex-wrap: wrap !important;
                    width: 100% !important;
                    overflow-x: auto !important;
                    padding: 2px 0 !important;
                }
                
                .cke_toolbar {
                    float: none !important;
                    display: inline-flex !important;
                    margin: 2px 0 !important;
                }
                
                /* Editor area */
                .cke_inner {
                    display: flex !important;
                    flex-direction: column !important;
                    width: 100% !important;
                }
                
                .cke_contents {
                    width: 100% !important;
                    min-height: 300px !important;
                    max-width: 100% !important;
                    box-sizing: border-box !important;
                }
                
                .cke_wysiwyg_frame, .cke_wysiwyg_div {
                    width: 100% !important;
                    max-width: 100% !important;
                }
                
                /* Content editable area */
                .cke_editable {
                    width: 100% !important;
                    max-width: 100% !important;
                    padding: 16px !important;
                    margin: 0 !important;
                    box-sizing: border-box !important;
                    overflow-wrap: break-word !important;
                    word-wrap: break-word !important;
                    line-height: 1.6 !important;
                    color: #374151 !important;
                    font-size: 14px !important;
                }
                
                /* Bottom bar */
                .cke_bottom {
                    background: #f9fafb !important;
                    border-top: 1px solid #e5e7eb !important;
                    padding: 4px 8px !important;
                }
                
                /* Remove resize handle */
                .cke_resizer_ltr, .cke_resizer {
                    display: none !important;
                }
                
                /* Fix for dialogs */
                .cke_dialog {
                    max-width: 90% !important;
                    width: auto !important;
                }
            `}</style>
        </div>
    );
}


const AdminNewsCreate = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [categories, setCategories] = useState([]);
  
  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    excerpt: '',
    content: '',
    category: '',
    author: 'Admin',
    status: 'draft',
    featured: false,
    date: new Date().toISOString().split('T')[0]
  });

  const [imageFile, setImageFile] = useState(null);

  const API_BASE_URL = "https://phucansolutions.vn" || '"https://phucansolutions.vn"';

  const statuses = [
    { value: 'draft', label: 'Nháp' },
    { value: 'published', label: 'Đã xuất bản' },
    { value: 'archived', label: 'Lưu trữ' }
  ];

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      const token = localStorage.getItem('admin_token');
      const response = await fetch(`${API_BASE_URL}/api/admin/categories`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      const data = await response.json();
      
      if (response.ok && data.success) {
        setCategories(data.data);
        // Set first category as default if available
        if (data.data.length > 0) {
          setFormData(prev => ({
            ...prev,
            category: data.data[0].slug
          }));
        }
      } else {
        // Fallback to hardcoded categories
        const fallbackCategories = [
          { slug: 'company-news', name: 'Tin công ty' },
          { slug: 'technology', name: 'Công nghệ' },
          { slug: 'project-updates', name: 'Cập nhật dự án' },
          { slug: 'industry-news', name: 'Tin ngành' },
          { slug: 'events', name: 'Sự kiện' }
        ];
        setCategories(fallbackCategories);
        setFormData(prev => ({
          ...prev,
          category: 'company-news'
        }));
      }
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Fallback to hardcoded categories
      const fallbackCategories = [
        { slug: 'company-news', name: 'Tin công ty' },
        { slug: 'technology', name: 'Công nghệ' },
        { slug: 'project-updates', name: 'Cập nhật dự án' },
        { slug: 'industry-news', name: 'Tin ngành' },
        { slug: 'events', name: 'Sự kiện' }
      ];
      setCategories(fallbackCategories);
      setFormData(prev => ({
        ...prev,
        category: 'company-news'
      }));
    }
  };

  // Auto generate slug from title
  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[đĐ]/g, 'd')
      .replace(/[^a-z0-9\s-]/g, '')
      .trim()
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-');
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    let newValue = type === 'checkbox' ? checked : value;

    setFormData(prev => ({
      ...prev,
      [name]: newValue
    }));

    // Auto-generate slug when title changes
    if (name === 'title') {
      setFormData(prev => ({
        ...prev,
        slug: generateSlug(value)
      }));
    }
  };

  const handleEditorChange = (content) => {
    setFormData(prev => ({
      ...prev,
      content
    }));
  };

  const handleImageChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        alert('Vui lòng chọn file hình ảnh hợp lệ');
        return;
      }

      // Validate file size (5MB max)
      if (file.size > 5 * 1024 * 1024) {
        alert('Kích thước file không được vượt quá 5MB');
        return;
      }

      setImageFile(file);
      
      // Create preview
      const reader = new FileReader();
      reader.onload = (e) => {
        setImagePreview(e.target.result);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeImage = () => {
    setImageFile(null);
    setImagePreview(null);
    document.getElementById('image-input').value = '';
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.title || !formData.content) {
      alert('Vui lòng điền đầy đủ tiêu đề và nội dung');
      return;
    }

    setLoading(true);

    try {
      const token = localStorage.getItem('admin_token');
      
      // Create FormData for file upload
      const submitData = new FormData();
      
      // Add all form fields
      Object.keys(formData).forEach(key => {
        if (formData[key] !== null && formData[key] !== undefined) {
          submitData.append(key, formData[key]);
        }
      });

      // Add image file if selected
      if (imageFile) {
        submitData.append('image', imageFile);
      }

      const response = await fetch(`${API_BASE_URL}/api/admin/news`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: submitData
      });

      const data = await response.json();

      if (response.ok && data.success) {
        alert('Tạo tin tức thành công!');
        navigate('/admin/news');
      } else {
        alert('Lỗi: ' + (data.error || 'Không thể tạo tin tức'));
      }
    } catch (error) {
      console.error('Error creating news:', error);
      alert('Lỗi kết nối server');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="admin-news-form">
      <div className="form-header">
        <div className="header-content">
          <h1>📝 Tạo Tin Tức Mới</h1>
          <p>Thêm tin tức mới cho website PhucAn Solutions</p>
        </div>
        <div className="header-actions">
          <button 
            type="button" 
            onClick={() => navigate('/admin/news')}
            className="cancel-btn"
          >
            ← Quay lại
          </button>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="news-form">
        <div className="form-grid">
          {/* Left Column */}
          <div className="form-column">
            {/* Basic Information */}
            <div className="form-section">
              <h3>📰 Thông tin cơ bản</h3>
              
              <div className="form-group">
                <label htmlFor="title">Tiêu đề *</label>
                <input
                  type="text"
                  id="title"
                  name="title"
                  value={formData.title}
                  onChange={handleInputChange}
                  placeholder="Nhập tiêu đề tin tức..."
                  required
                />
              </div>

              <div className="form-group">
                <label htmlFor="slug">URL Slug</label>
                <input
                  type="text"
                  id="slug"
                  name="slug"
                  value={formData.slug}
                  onChange={handleInputChange}
                  placeholder="url-slug-tu-dong-tao"
                />
                <small>URL hiển thị trên website. Để trống để tự động tạo từ tiêu đề</small>
              </div>

              <div className="form-group">
                <label htmlFor="excerpt">Tóm tắt</label>
                <textarea
                  id="excerpt"
                  name="excerpt"
                  value={formData.excerpt}
                  onChange={handleInputChange}
                  placeholder="Mô tả ngắn về tin tức..."
                  rows="3"
                />
                <small>Mô tả ngắn hiển thị trong danh sách tin tức</small>
              </div>

              <div className="form-group">
                <label htmlFor="content">Nội dung *</label>
                <CKEditorLegacy 
                  initialData={formData.content} 
                  onDataChange={handleEditorChange} 
                />
                <small>Nội dung chi tiết của tin tức</small>
              </div>
            </div>

            {/* Featured Image */}
            <div className="form-section">
              <h3>🖼️ Hình ảnh đại diện</h3>
              
              <div className="image-upload-section">
                {imagePreview ? (
                  <div className="image-preview">
                    <img src={imagePreview} alt="Preview" />
                    <button 
                      type="button" 
                      onClick={removeImage}
                      className="remove-image-btn"
                    >
                      🗑️ Xóa ảnh
                    </button>
                  </div>
                ) : (
                  <div className="image-upload-placeholder">
                    <div className="upload-icon">📷</div>
                    <p>Chưa có hình ảnh</p>
                  </div>
                )}
                
                <input
                  type="file"
                  id="image-input"
                  accept="image/*"
                  onChange={handleImageChange}
                  style={{ display: 'none' }}
                />
                
                <button 
                  type="button"
                  onClick={() => document.getElementById('image-input').click()}
                  className="upload-btn"
                >
                  📁 Chọn hình ảnh
                </button>
                
                <small>Định dạng: JPG, PNG, GIF. Kích thước tối đa: 5MB</small>
              </div>
            </div>
          </div>

          {/* Right Column */}
          <div className="form-column">
            {/* Publishing Options */}
            <div className="form-section">
              <h3>⚙️ Tùy chọn xuất bản</h3>
              
              <div className="form-group">
                <label htmlFor="status">Trạng thái</label>
                <select
                  id="status"
                  name="status"
                  value={formData.status}
                  onChange={handleInputChange}
                >
                  {statuses.map(status => (
                    <option key={status.value} value={status.value}>
                      {status.label}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="category">Danh mục</label>
                <select
                  id="category"
                  name="category"
                  value={formData.category}
                  onChange={handleInputChange}
                >
                  {categories.map(cat => (
                    <option key={cat.slug} value={cat.slug}>
                      {cat.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label htmlFor="author">Tác giả</label>
                <input
                  type="text"
                  id="author"
                  name="author"
                  value={formData.author}
                  onChange={handleInputChange}
                  placeholder="Tên tác giả"
                />
              </div>

              <div className="form-group">
                <label htmlFor="date">Ngày xuất bản</label>
                <input
                  type="date"
                  id="date"
                  name="date"
                  value={formData.date}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group checkbox-group">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="featured"
                    checked={formData.featured}
                    onChange={handleInputChange}
                  />
                  <span className="checkmark"></span>
                  <span className="label-text">⭐ Tin tức nổi bật</span>
                </label>
                <small>Tin tức sẽ được hiển thị ở vị trí nổi bật</small>
              </div>
            </div>

            {/* SEO Preview */}
            <div className="form-section">
              <h3>🔍 Xem trước SEO</h3>
              <div className="seo-preview">
                <div className="seo-title">
                  {formData.title || 'Tiêu đề tin tức'}
                </div>
                <div className="seo-url">
                  https://phucan-solutions.com/news/{formData.slug || 'url-slug'}
                </div>
                <div className="seo-description">
                  {formData.excerpt || 'Mô tả tin tức sẽ hiển thị ở đây...'}
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Form Actions */}
        <div className="form-actions">
          <button 
            type="button" 
            onClick={() => navigate('/admin/news')}
            className="cancel-btn"
            disabled={loading}
          >
            Hủy bỏ
          </button>
          
          <button 
            type="submit" 
            className="submit-btn"
            disabled={loading}
          >
            {loading ? '🔄 Đang tạo...' : '✅ Tạo tin tức'}
          </button>
        </div>
      </form>
    </div>
  );
};

export default AdminNewsCreate; 